import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Eye, FileText, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getClientCandidates,
  getActiveEmployees,
  assignCandidateToEmployee,
} from '../../../services/admin/api';
import './ClientCandidates.css';

const AdminCandidates = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const heading = location.state?.heading || 'Admin Candidates';
  const companyName = location.state?.companyName || clientId;

  const [candidates, setCandidates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');

  const filteredEmployees = employees.filter(employee =>
    employee.name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    employee.id.toString().includes(employeeSearchTerm.toLowerCase())
  );

  useEffect(() => {
    const fetchCandidates = async () => {
      //console.log(`[AdminCandidates] Fetching candidates for client ID: ${clientId}, page: ${page}`);
      try {
        const response = await getClientCandidates(clientId, page, 10);
        //console.log('[AdminCandidates] Raw API response for candidates:', response);

        if (typeof response === 'string') {
          console.error('[AdminCandidates] API returned a string error:', response);
          throw new Error(response);
        }

        if (response && Array.isArray(response.content)) {
          setCandidates(response.content);
          setTotalPages(response.totalPages || 0);
          //console.log('[AdminCandidates] Candidates set to state:', response.content);
        } else {
          console.warn('[AdminCandidates] Unexpected candidates response structure:', response);
          setCandidates([]);
          setTotalPages(0);
        }
      } catch (error) {
        console.error('[AdminCandidates] Error fetching admin candidates:', error);
        if (
          error?.response?.status === 503 ||
          error?.message?.includes('Client service is unavailable')
        ) {
          navigate('/admin/clients');
        } else {
          toast.error('Failed to fetch candidates.');
        }
      }
    };

    fetchCandidates();
  }, [clientId, page, navigate]);

  const handleSubmitAssignment = async (employeeId) => {
    if (!selectedCandidate) {
      toast.error('No candidate selected for assignment.');
      return;
    }

    //console.log('[Check] Candidate has phone:',selectedCandidate.mobileNumber ? selectedCandidate.mobileNumber : 'NO PHONE PROVIDED');

    const payload = {
      candidateId: selectedCandidate.candidateid,
      employeeId: employeeId,
      adminId: 1, // Replace with actual logged-in admin ID
      userid: selectedCandidate.userid,
      snapshot: {
        fullName: selectedCandidate.name,
        email: selectedCandidate.email,
        phone: selectedCandidate.mobileNumber || '',
      },
    };

    //console.log('[AdminCandidates] Assignment Payload being sent:', payload);

    try {
      const res = await assignCandidateToEmployee(payload);
      //console.log('[AdminCandidates] Assignment API response:', res);
      toast.success(res.message || 'Candidate assigned successfully.');
      setShowSuccess(true);
      const updatedCandidatesResponse = await getClientCandidates(clientId, page, 10);
      setCandidates(updatedCandidatesResponse.content || []);
      //console.log('[AdminCandidates] Candidates re-fetched after assignment.');
    } catch (error) {
      console.error('[AdminCandidates] Assignment failed:', error);
      toast.error('Failed to assign candidate.');
    }
  };

  const getStatusTextClass = (status) => {
    switch (status) {
      case 'COMPLETED': return 'admin-status-text green';
      case 'WORK_IN_PROGRESS': return 'admin-status-text orange';
      case 'YET_TO_START': return 'admin-status-text purple';
      case 'VERIFIED': return 'admin-status-text blue';
      case 'PENDING_SUBMISSION': return 'admin-status-text gray';
      default: return 'admin-status-text gray';
    }
  };

  const getButtonClass = (status) => {
    switch (status) {
      case 'COMPLETED': return 'admin-assign-btn green';
      case 'WORK_IN_PROGRESS': return 'admin-assign-btn orange';
      case 'YET_TO_START': return 'admin-assign-btn purple';
      case 'VERIFIED': return 'admin-assign-btn green';
      case 'PENDING_SUBMISSION': return 'admin-assign-btn gray';
      default: return 'admin-assign-btn gray';
    }
  };

  const getButtonLabel = (status) => {
    switch (status) {
      case 'WORK_IN_PROGRESS': return 'Reassign';
      case 'COMPLETED':
      case 'VERIFIED': return 'Completed';
      case 'YET_TO_START':
      case 'PENDING_SUBMISSION': return 'Assign';
      default: return 'Assign';
    }
  };

  const isButtonDisabled = (status) => {
    return status === 'COMPLETED' || status === 'VERIFIED' || status === 'PENDING_SUBMISSION';
  };

  const handleAssignClick = async (candidate) => {
    //console.log('[AdminCandidates] Assign button clicked for candidate:', candidate);
    try {
      const employeeData = await getActiveEmployees();
      //console.log('[AdminCandidates] Fetched active employees:', employeeData);
      setEmployees(employeeData);
      setSelectedCandidate(candidate);
      setShowModal(true);
      setShowSuccess(false);
      setEmployeeSearchTerm('');
    } catch (error) {
      console.error('[AdminCandidates] Failed to fetch active employees:', error);
      toast.error('Failed to load employees.');
    }
  };

  return (
    <div className="admin-candidates-wrapper">
      <header className="admin-header">
      </header>

      <main className="admin-main">
        <div className="admin-card">
          
          {/* ✅ Client Info Section added here */}
          <div className="client-info-section">
            <p><strong>Client Name:</strong> {location.state?.clientName || 'N/A'}</p>
            <p><strong>Company Name:</strong> {companyName}</p>
            <p><strong>Client User ID:</strong> {clientId || 'N/A'}</p>
          </div>

          <div className="admin-table-container">
            <table>
              <thead>
                <tr>
                  <th>Unique ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Current BGC Status</th>
                  <th>Assign</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {candidates.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center' }}>
                      No candidates found.
                    </td>
                  </tr>
                ) : (
                  candidates.map((candidate, idx) => (
                    <tr key={idx}>
                      <td>{candidate.candidateid}</td>
                      <td>{candidate.name}</td>
                      <td>{candidate.email}</td>
                      <td>
                        <span className={getStatusTextClass(candidate.bgcStatus)}>
                          {candidate.bgcStatus || 'Unknown'}
                        </span>
                      </td>
                      <td>
                        <button
                          className={getButtonClass(candidate.bgcStatus)}
                          onClick={() => handleAssignClick(candidate)}
                          disabled={isButtonDisabled(candidate.bgcStatus)}
                        >
                          {getButtonLabel(candidate.bgcStatus)}
                        </button>
                      </td>
                      <td className="action-icons">
                        <button
                          className="icon-btn"
                          title={
                            candidate.bgcStatus === 'VERIFIED'
                              ? 'View Candidate Report'
                              : 'Report available only after verification'
                          }
                          disabled={candidate.bgcStatus !== 'VERIFIED'}
                          onClick={() =>
                            navigate(`/admin/reports/${candidate.candidateid}`, {
                              state: {
                                heading: 'Candidate Report',
                                candidate: candidate,
                              },
                            })
                          }
                        >
                          <FileText size={18} color={candidate.bgcStatus === 'VERIFIED' ? 'black' : 'gray'} />
                        </button>

                        <button
                          className="icon-btn"
                          title="View Candidate Details"
                          onClick={() =>
                            navigate(`/admin/candidate-details/${candidate.candidateid}`, {
                              state: { candidate: candidate },
                            })
                          }
                        >
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="admin-pagination-controls">
            <button
              className="admin-pagination-btn"
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              disabled={page === 0}
            >
              <ChevronLeft size={16} />
            </button>
            <span className="admin-page-info">
              Page {page + 1} of {totalPages}
            </span>
            <button
              className="admin-pagination-btn"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages - 1))}
              disabled={page + 1 >= totalPages}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </main>

      {showModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content admin-assign-employee-modal">
            {!showSuccess ? (
              <>
                <h3>Assign Candidate</h3>
                  <p>
                    <strong>Name:</strong> {selectedCandidate?.name} &nbsp; | &nbsp;
                    <strong>Email:</strong> {selectedCandidate?.email} &nbsp; | &nbsp;
                    <strong>Candidate ID:</strong> {selectedCandidate?.candidateid}
                  </p>


                <div className="admin-glass-search-wrapper">
                  <span className="admin-glass-search-icon">
                    <Search size={18} />
                  </span>
                  <input
                    type="text"
                    placeholder="Search by Employee ID or Name"
                    value={employeeSearchTerm}
                    onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                    className="admin-glass-search-input"
                  />
                </div>

                <div className="admin-employee-table-wrapper">
                  <table className="admin-employee-selection-table-header">
                    <thead>
                      <tr>
                        <th>Agent ID</th>
                        <th>Agent Name</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                  </table>
                  <div className="admin-table-body-scroll-wrapper">
                    {filteredEmployees.length > 0 ? (
                      <table className="admin-employee-selection-table-body">
                        <tbody>
                          {filteredEmployees.map((emp) => (
                            <tr key={emp.id}>
                              <td>{emp.id}</td>
                              <td>{emp.name}</td>
                              <td>
                                <button
                                  className="admin-assign-btn admin-assign-confirm-modal"
                                  onClick={() => handleSubmitAssignment(emp.id)}
                                >
                                  Assign
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="admin-no-results-message">No employees found matching your search.</p>
                    )}
                  </div>
                </div>

                <div className="admin-modal-buttons">
                  <button className="admin-assign-cancel" onClick={() => setShowModal(false)}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>✅ Successfully Assigned</h3>
                <p>{selectedCandidate?.name} has been assigned.</p>
                <div className="admin-modal-buttons">
                  <button
                    className="admin-assign-confirm"
                    onClick={() => {
                      setShowModal(false);
                      setShowSuccess(false);
                    }}
                  >
                    OK
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCandidates;
